import { Box, Button, Card, CardContent, Tooltip, Typography } from "@mui/material";
import GroupVideoBg from "../../../assets/Group_video_bg.svg";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { _AuthType } from "../../../enums";
import { useContext, useMemo } from "react";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { ComputedQuizScore, CourseStructureCtx, UserCtx } from "../../../interfaces";
import { Target } from "../../utils/CourseTree";
import { UserContext } from "../../../context/UserContext";

interface ModuleCarouselProps {
  moduleId: string;
  title: string;
  moduleNumber: number;
  isModuleLocked: boolean;
  isPreviousModule: boolean;
  isNextModule: boolean;
  moduleScore: number;
  onFocus: () => void;
}

const useStyles = makeStyles((theme) => ({
  moduleIndicator: {
    padding: "4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "24px",
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    width: "fit-content",
    marginBottom: "16px",
  },
}));
export const ModuleCarousel = (props: ModuleCarouselProps) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    moduleId,
    title,
    moduleNumber,
    isModuleLocked,
    isPreviousModule,
    isNextModule,
    onFocus,
  } = props;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const metadata = courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const currentModule = courseStructureCtx.courseTreeStructure
    ?.getAllModulesInCourse()
    .find((item) => item.id === moduleId);
  const userCtx = useContext(UserContext) as UserCtx;

  const openModules = () => {
    const targetElement = document.getElementsByTagName("header");
    if (targetElement.length > 0) {
      targetElement[0].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
    history.push({
      pathname: "/modules",
      search: "moduleId=" + moduleId,
    });
  };

  const quizScore = useMemo<ComputedQuizScore>(() => {
    const targets = currentModule?.children as Target[];
    return userCtx.calculateQuizScore(targets);
  }, [currentModule, userCtx]);

  const scoreDisplay = () => {
    switch (quizScore?.invalid) {
      case "No Quiz":
        return "No Quiz";
      case "-":
        return "-";
      case undefined:
      default:
        return quizScore.display_as === "percentage"
          ? `${Math.round((quizScore.percentage ?? 0) * 100)}%`
          : quizScore.display_as === "points"
          ? `${quizScore.points ?? 0}/${quizScore.max_points ?? 0}`
          : "-";
    }
  };

  const handleActivate = () => {
    if (!isModuleLocked) {
      openModules();
      return;
    }

    const { authType, ltiReturnUrl } = MacsAuthenticator.authHook;

    if (authType === _AuthType.LTI && ltiReturnUrl) {
      window.location.href = ltiReturnUrl;
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "204px",
        width: "236px",
        marginRight: "12px",
        backgroundImage: `url(${GroupVideoBg})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        filter: isModuleLocked === true ? "grayscale(100%)" : "none",
        borderRadius: "16px",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          backgroundColor: "transparent",
          transition: "background-color 0.3s ease",
        },
        "&:hover::after": {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        },
        "&:hover .hover-button": {
          display: "flex",
        },
      }}
      tabIndex={0}
      role="button"
      onFocus={onFocus}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}>
      <Card
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          position: "relative",
          height: "100%",
          width: "100%",
          padding: "24px 32px",
          border: "none",
        }}>
        <CardContent
          sx={{
            height: "100%",
            padding: "0px !important",
            display: "flex",
            flexDirection: "column",
          }}>
          {!metadata?.module_overview_page?.disable_module_number && (
            <Box className={`${classes.moduleIndicator}`}>
              <Typography
                variant="body1"
                lineHeight="1em"
                textTransform="uppercase"
                noWrap>
                {isPreviousModule
                  ? `Previous : Module ${moduleNumber}`
                  : isNextModule
                  ? `Next : Module ${moduleNumber}`
                  : `Module ${moduleNumber}`}
              </Typography>
            </Box>
          )}
          <Box sx={{ maxHeight: "76px", overflow: "hidden" }}>
            <Typography
              component="span"
              variant="h4"
              sx={{
                color: "#ffffff",
                marginBottom: "16px",
                fontWeight: 700,
                fontSize: "20px",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                WebkitLineClamp: 3,
                textOverflow: "ellipsis",
              }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ marginTop: "auto" }}>
            <Typography sx={{ color: "#ffffff" }} textTransform="uppercase">
              Score: {scoreDisplay()}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {isModuleLocked === true ? (
        MacsAuthenticator.authHook.authType === _AuthType.LTI &&
        MacsAuthenticator.authHook.ltiReturnUrl && (
          <Tooltip
            title="Great progress! Launch from Canvas to unlock."
            placement="bottom"
            arrow>
            {/**TODO: Even the button is greyed because the parent is greyed. Fix this */}
            <Button
              type="button"
              className="hover-button"
              variant="outlined"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                const returnUrl = MacsAuthenticator.authHook.ltiReturnUrl;
                if (returnUrl) window.location.href = returnUrl;
              }}
              sx={{
                position: "absolute",
                display: "none",
                zIndex: 1,
              }}>
              Back to Canvas
            </Button>
          </Tooltip>
        )
      ) : (
        <Button
          type="button"
          className="hover-button"
          variant="outlined"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            openModules();
          }}
          sx={{
            position: "absolute",
            display: "none",
            zIndex: 1,
          }}>
          Explore
        </Button>
      )}
    </Box>
  );
};
